<template>
  <Tippy v-bind="props" theme="tooltip">
    <slot>
      <UIIcon class="UITooltip__icon" name="tooltip" />
    </slot>

    <template #content>
      <slot name="content" />
    </template>
  </Tippy>
</template>

<script lang="ts" setup>
import { Tippy } from 'vue-tippy'

import UIIcon from '../icons/UIIcon.vue'
import 'tippy.js/dist/tippy.css'

type ITippyProps = InstanceType<typeof Tippy>['$props']

// `theme` isn't exposed: this shim only ever renders the one (ui-lib-styled) look, applied
// via the fixed `theme="tooltip"` on Tippy above so the global `.tippy-box[data-theme~=…]`
// styling still matches. Everything else (placement, arrow, delay…) falls through to Tippy.
interface Props extends /* @vue-ignore */ ITippyProps {
  interactive?: boolean
  appendTo?: Element | 'parent' | ((ref: Element) => Element)
  offset?: [number, number]
}

const props = withDefaults(defineProps<Props>(), {
  appendTo: () => () => document.body,
  interactive: true,
  // Matches the ui-lib tooltip's [skid, distance] gap so the later swap is seamless.
  offset: () => [0, 5],
})
</script>

<style scoped lang="scss">
.UITooltip__icon {
  --size: 1rem;
}
</style>

<style lang="scss">
// The local tooltip mirrors the @foundever/ui-lib UITooltip (dark theme) so it can later be
// swapped for the real ui-lib component with no visual change. Values track ui-lib's own
// design tokens (loaded via its style.css + Inter face) with literal fallbacks. The ui-lib
// tooltip has no arrow, so it's hidden here rather than left for each caller to switch off.
.tippy-box[data-theme~='tooltip'] {
  // Tippy applies its `maxWidth` option as an inline style, so `!important` is needed to pin
  // the ui-lib width — the same override the dropdown theme already relies on.
  max-width: 15rem !important;
  border-radius: var(--radius-s, 0.5rem);
  background-color: var(--Color-Neutral-NEW-90, #1b1b46);
  color: var(--text-inverted, #fff);
  box-shadow: var(--shadow-md, 0 0.5rem 0.9375rem -0.25rem #09092d21);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  .tippy-content {
    padding: var(---fds-global-space-xxs, 0.25rem)
      var(---fds-global-space-xs, 0.5rem);
    word-break: break-word;

    a {
      color: inherit;
      text-decoration: none;
    }
  }

  & > .tippy-arrow {
    display: none;
  }
}

.tippy-box > .tippy-arrow {
  position: absolute;
  width: 9px;
  height: 9px;
  box-sizing: border-box;
  background: $color-white;
  border: none;

  &:before {
    position: absolute;
    width: 9px;
    height: 9px;
    box-sizing: border-box;
    background: $color-white;
    transform: rotate(45deg);
    border: 0;
  }
}

.tippy-box[data-placement^='bottom'] > .tippy-arrow:before {
  border-left: 1px solid $color-white;
  border-top: 1px solid $color-white;
  left: -3px;
}

.tippy-box[data-placement^='top'] > .tippy-arrow:before {
  border-right: 1px solid $color-white;
  border-bottom: 1px solid $color-white;
  left: 3px;
  top: 3px;
}

.tippy-box[data-placement^='left'] > .tippy-arrow:before {
  border-top: 1px solid $color-white;
  border-right: 1px solid $color-white;
  top: -3px;
}

.tippy-box[data-placement^='right'] > .tippy-arrow:before {
  border-bottom: 1px solid $color-white;
  border-left: 1px solid $color-white;
  top: 3px;
}
</style>
